<script setup lang="ts">
import { type RangeValue } from 'v-customizable-date-picker';
import DatepickerCalendar from '@components/base/mr-datepicker/DatepickerCalendar.vue';
import type HotelPackage from '@itinerary/hotel/HotelPackage';
import { CalendarType } from '@utils/itinerary';
import { type HotelPathData } from '@utils/hotel';
import DatepickerHelpers from '@components/base/mr-datepicker/DatepickerHelpers.vue';

const { pkg } = defineProps<{ pkg: HotelPackage }>();

const hotelForm = useHotelForm();
const invoiceStore = useInvoiceStore();
const hotelModule = useHotelModule();
const searchFormStore = useSearchFormStore();
const router = useRouter();
const analyticsStore = useAnalyticsStore();
const route = useRoute();
const { isMobile } = useSize();

const loading = ref(false);

const rangeDate = computed<RangeValue>({
    get() {
        return {
            start: hotelForm.fromDate,
            end: hotelForm.toDate,
        };
    },
    set(value: RangeValue) {
        if (value.start) {
            hotelForm.fromDate = value.start;
        }
        hotelForm.toDate = value.end;
    },
});

const pathData = computed(
    () => ({ ...route.params, ...route.query }) as HotelPathData
);

const HotelSchedulePrices = computed(() => {
    const info = {} as Record<string, DayConfig>;

    pkg.HotelSchedule.forEach(item => {
        const { price } = item;
        const key = dayConfig.gregory(item.date || '').format(formatter());
        info[key] = {
            label: price
                ? `${formatMoney(price, {
                      includeUnit: false,
                      unit: 'KToman',
                  })} ت`
                : 'تکمیل',
            labelColor: {
                main: price ? 'var(--cool-gray-700)' : 'var(--red-400)',
            },
        };
    });
    return info;
});

const loadHotelSchedule = async () => {
    loading.value = true;
    await pkg.loadHotelSchedule();
    analyticsStore.trackAll(
        'no-capacity-calendar-init',
        analyticsStore.searchResultsAttributes
    );
    loading.value = false;
};
const searchRooms = () => {
    const urlParams = hotelForm.parsePath(pathData.value);
    if (urlParams && rangeDate.value.start && rangeDate.value.end) {
        invoiceStore.clearAllEntries();
        hotelModule.changeCurrentSelectedPackageInfo({
            fromDate: rangeDate.value.start.toISOString(),
            toDate: rangeDate.value.end.toISOString(),
        });
        hotelModule.loadCurrentSelectedPackageRooms();
        searchFormStore.urlParams = {
            ...urlParams,
            fromDate: dayConfig
                .gregory(rangeDate.value.start)
                .format(formatter()),
            toDate: dayConfig.gregory(rangeDate.value.end).format(formatter()),
        };

        router.replace({
            query: {
                ...route.query,
                fromDate: dayConfig
                    .jalali(rangeDate.value.start)
                    .format(formatter()),
                toDate: dayConfig
                    .jalali(rangeDate.value.end)
                    .format(formatter()),
            },
        });
        analyticsStore.trackAll(
            'no-capacity-calendar-search',
            analyticsStore.searchResultsAttributes
        );
    }
};

const currentCalendar = computed(() => {
    switch (dayConfig.calendar) {
        case CalendarType.Gregorian:
            return 1;
        case CalendarType.Jalali:
        default:
            return 0;
    }
});

const nights = computed(() => {
    if (!rangeDate.value.start || !rangeDate.value.end) {
        return '';
    }
    return `${dayConfig
        .gregory(rangeDate.value.end)
        .startOf('day')
        .diff(
            dayConfig.gregory(rangeDate.value.start).startOf('day'),
            'day'
        )} شب اقامت`;
});

onMounted(loadHotelSchedule);
</script>

<template>
    <DatepickerCalendar
        v-model="rangeDate"
        :current-calendar
        :min="new Date()"
        range-start-day-tooltip="ورود"
        range-end-day-tooltip="خروج"
        range
        :loading
        :month-count="isMobile ? 1 : 2"
        unequal-range
        disable-mobile-month-list
        :day-config="HotelSchedulePrices"
    />
    <div class="schedule-tooltip">
        <DatepickerHelpers
            class="schedule-tips"
            night-count-helper
            :nights
            divide
        />

        <div class="actions">
            <mr-button
                class="action-btn"
                variant="bordered"
                @click="switchCalendar"
            >
                تبدیل به
                {{ dayConfig.calendar === 'jalali' ? 'میلادی' : 'شمسی' }}
            </mr-button>
            <mr-button class="action-btn" @click="searchRooms">
                تایید
            </mr-button>
        </div>
    </div>
</template>

<style scoped lang="scss">
:deep(.main-header-container) {
    position: relative !important;
}

.schedule-tooltip {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 4px;
    .schedule-tips {
        justify-content: right !important;
    }
    .actions {
        display: flex;
        align-items: center;
        gap: 8px;
        .action-btn {
            width: 112px;
        }
    }
}
.schedule,
.re-search {
    width: 50%;
}
</style>
